import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Navbar from "../components/Navbar";
import CreateUserModal from "../components/admin/CreateUserModal";
import ResetPasswordModal from "../components/admin/ResetPasswordModal";
import ProgressModal from "../components/admin/ProgressModal";
import DigestCard from "../components/admin/DigestCard";
import LeadsPanel from "../components/admin/LeadsPanel";
import { toast } from "sonner";
import axios from "axios";
import {
  Users,
  Plus,
  ShieldCheck,
  ShieldOff,
  KeyRound,
  Eye,
  ArrowLeft,
  Trash2,
  Archive,
  ChevronDown,
  RotateCcw,
  Info,
} from "lucide-react";
import { ScrollArea } from "../components/ui/scroll-area";
import { log } from "../utils/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showReset, setShowReset] = useState(null);
  const [showProgress, setShowProgress] = useState(null);
  const [progressData, setProgressData] = useState(null);
  const [resetRequests, setResetRequests] = useState([]);
  const [serviceInquiries, setServiceInquiries] = useState([]);
  const [archivedInquiries, setArchivedInquiries] = useState([]);
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) navigate("/quickstart");
  }, [loading, user, navigate]);

  const loadUsers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setUsers(data);
    } catch {
      toast.error("Failed to load users");
    }
  }, []);

  const loadInquiries = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/inquiries`, { withCredentials: true });
      setServiceInquiries(data.filter((i) => i.status === "new"));
      setArchivedInquiries(data.filter((i) => i.status !== "new"));
    } catch (err) {
      log.warn("Failed to load inquiries", err);
    }
  }, []);

  const loadResetRequests = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/reset-requests`, { withCredentials: true });
      setResetRequests(data);
    } catch (err) {
      log.warn("Failed to load reset requests", err);
    }
  }, []);

  useEffect(() => {
    if (user?.role === "admin") {
      loadUsers();
      loadResetRequests();
      loadInquiries();
    }
  }, [user, loadUsers, loadResetRequests, loadInquiries]);

  const resolveInquiry = async (email) => {
    try {
      await axios.put(`${API}/admin/inquiries/${encodeURIComponent(email)}/resolve`, {}, { withCredentials: true });
      toast.success("Inquiry archived");
      loadInquiries();
    } catch { toast.error("Failed"); }
  };

  const reopenInquiry = async (email) => {
    try {
      await axios.put(`${API}/admin/inquiries/${encodeURIComponent(email)}/reopen`, {}, { withCredentials: true });
      toast.success("Inquiry reopened");
      loadInquiries();
    } catch { toast.error("Failed to reopen"); }
  };

  const resolveResetRequest = async (email) => {
    try {
      await axios.post(`${API}/admin/reset-requests/${email}/resolve`, {}, { withCredentials: true });
      toast.success("Request resolved");
      loadResetRequests();
    } catch { toast.error("Failed"); }
  };

  const toggleActive = async (userId) => {
    try {
      const { data } = await axios.put(`${API}/admin/users/${userId}/toggle-active`, {}, { withCredentials: true });
      toast.success(data.message);
      loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const deleteUser = async (userId, email) => {
    if (!window.confirm(`Delete ${email}? This will permanently remove the account and all their progress.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
      toast.success("User deleted");
      loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
  };

  const viewProgress = async (userId) => {
    setShowCreate(false);
    setShowReset(null);
    try {
      const { data } = await axios.get(`${API}/admin/users/${userId}/progress`, { withCredentials: true });
      setProgressData(data);
      setShowProgress(userId);
    } catch { toast.error("Failed to load progress"); }
  };

  if (loading || !user || user.role !== "admin") return null;

  const customers = users.filter((u) => u.role !== "admin");
  const activeCount = customers.filter((u) => u.is_active !== false).length;

  return (
    <div className="flex min-h-screen flex-col bg-[var(--c-bg)]">
      <Navbar />
      <div className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-6">
        {/* Header */}
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-back-button"
              onClick={() => navigate("/quickstart")}
              className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-[var(--c-border)] bg-[var(--c-surface)] shadow-[3px_3px_0px_var(--c-shadow)] transition-all hover:-translate-y-0.5"
            >
              <ArrowLeft className="h-4 w-4 text-[var(--c-text)]" />
            </button>
            <div>
              <h1 className="font-heading text-2xl font-black text-[var(--c-text)] sm:text-3xl">
                Customer Management
              </h1>
              <p className="text-sm text-[var(--c-muted)]">
                {customers.length} customer{customers.length !== 1 ? "s" : ""} &middot; {activeCount} active
              </p>
            </div>
          </div>
          <button
            data-testid="create-user-button"
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 rounded-full border-2 border-[var(--c-border)] bg-[#1E3A8A] px-5 py-2.5 font-bold text-white shadow-[3px_3px_0px_var(--c-shadow)] transition-all hover:-translate-y-0.5"
          >
            <Plus className="h-4 w-4" />
            Create Customer
          </button>
        </div>

        {/* Password Reset Requests */}
        {resetRequests.length > 0 && (
          <div data-testid="reset-requests-section" className="mb-6 rounded-xl border-2 border-[#1E3A8A]/30 bg-[#1E3A8A]/5 p-5 shadow-[3px_3px_0px_var(--c-shadow)]">
            <h3 className="mb-3 font-heading text-base font-bold text-[var(--c-text)]">
              Password Reset Requests ({resetRequests.length})
            </h3>
            <div className="space-y-2">
              {resetRequests.map((req) => (
                <div key={`reset-${req.email}-${req.created_at}`} className="flex items-center justify-between rounded-lg border-2 border-[var(--c-border-light)] bg-[var(--c-surface)] px-4 py-3">
                  <div>
                    <p className="text-sm font-semibold text-[var(--c-text)]">{req.name || req.email}</p>
                    <p className="text-xs text-[var(--c-muted)]">{req.email} &middot; {new Date(req.created_at).toLocaleString()}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      data-testid={`reset-for-${req.email}`}
                      onClick={() => {
                        const u = users.find((usr) => usr.email === req.email);
                        if (u) { setShowReset(u); }
                      }}
                      className="rounded-full border-2 border-[var(--c-border)] bg-[#1E3A8A] px-3 py-1 text-xs font-bold text-white shadow-[2px_2px_0px_var(--c-shadow)] transition-all hover:-translate-y-0.5"
                    >
                      Reset Password
                    </button>
                    <button
                      data-testid={`resolve-${req.email}`}
                      onClick={() => resolveResetRequest(req.email)}
                      className="rounded-full border-2 border-[var(--c-border-light)] px-3 py-1 text-xs font-bold text-[var(--c-muted)] transition-all hover:border-[var(--c-border)] hover:shadow-[2px_2px_0px_var(--c-shadow)]"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Inquiry digest */}
        <DigestCard />

        {/* Checklist leads */}
        <LeadsPanel />

        {/* Service Inquiries */}
        {serviceInquiries.length > 0 && (
          <div data-testid="service-inquiries-section" className="mb-6 rounded-xl border-2 border-[#A3E6D5]/50 bg-[#A3E6D5]/5 p-5 shadow-[3px_3px_0px_var(--c-shadow)]">
            <h3 className="mb-3 font-heading text-base font-bold text-[var(--c-text)]">
              Service Inquiries ({serviceInquiries.length})
            </h3>
            <div className="space-y-2">
              {serviceInquiries.map((inq) => (
                <div key={`inq-${inq.email}-${inq.created_at}`} className="rounded-lg border-2 border-[var(--c-border-light)] bg-[var(--c-surface)] px-4 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-[var(--c-text)]">{inq.name}</p>
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${inq.tier === "full" ? "bg-[#1E3A8A]/10 text-[#1E3A8A]" : "bg-[var(--c-hover-bg)] text-[var(--c-muted)]"}`}>
                          {inq.tier === "full" ? "Full Setup $199" : "Basic $99"}
                        </span>
                      </div>
                      <p className="text-xs text-[var(--c-muted)]">
                        <a href={`mailto:${inq.email}`} className="underline decoration-dotted underline-offset-2 hover:text-[#1E3A8A]">{inq.email}</a>
                        {" "}&middot; {new Date(inq.created_at).toLocaleString()}
                      </p>
                      {inq.message && <p className="mt-1 text-xs text-[var(--c-text)]">&ldquo;{inq.message}&rdquo;</p>}
                    </div>
                    <button
                      data-testid={`resolve-inquiry-${inq.email}`}
                      onClick={() => resolveInquiry(inq.email)}
                      title="Archive this inquiry (it will be moved to the Archived section — nothing is deleted)"
                      className="flex-shrink-0 rounded-full border-2 border-[var(--c-border-light)] px-3 py-1 text-xs font-bold text-[var(--c-muted)] transition-all hover:border-[var(--c-border)] hover:shadow-[2px_2px_0px_var(--c-shadow)]"
                    >
                      Resolve
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div data-testid="inquiries-info-note" className="mt-3 flex items-start gap-2 rounded-lg border border-dashed border-[var(--c-border-medium)] bg-[var(--c-bg)] px-3 py-2 text-xs text-[var(--c-muted)]">
              <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[#1E3A8A]" />
              <p>
                Customer inquiries currently appear <strong>only here</strong> — no email notification is sent. Check this dashboard regularly, or ask to enable email forwarding to <span className="font-mono">info@texasllcguide.llc</span>.
              </p>
            </div>
          </div>
        )}

        {/* Archived Inquiries — collapsible */}
        {archivedInquiries.length > 0 && (
          <div data-testid="archived-inquiries-section" className="mb-6 rounded-xl border-2 border-[var(--c-border)] bg-[var(--c-surface)] p-5 shadow-[3px_3px_0px_var(--c-shadow)]">
            <button
              data-testid="toggle-archived-inquiries"
              onClick={() => setShowArchived((v) => !v)}
              className="flex w-full items-center justify-between gap-3"
              aria-expanded={showArchived}
            >
              <span className="flex items-center gap-2">
                <Archive className="h-4 w-4 text-[var(--c-muted)]" />
                <span className="font-heading text-base font-bold text-[var(--c-text)]">
                  Archived Inquiries ({archivedInquiries.length})
                </span>
              </span>
              <ChevronDown className={`h-4 w-4 text-[var(--c-muted)] transition-transform ${showArchived ? "rotate-180" : ""}`} />
            </button>
            {showArchived && (
              <div data-testid="archived-inquiries-list" className="mt-4 space-y-2">
                {archivedInquiries.map((inq) => (
                  <div key={`arc-${inq.email}-${inq.created_at}`} className="rounded-lg border-2 border-[var(--c-border-light)] bg-[var(--c-bg)] px-4 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-sm font-semibold text-[var(--c-text)]">{inq.name}</p>
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${inq.tier === "full" ? "bg-[#1E3A8A]/10 text-[#1E3A8A]" : "bg-[var(--c-hover-bg)] text-[var(--c-muted)]"}`}>
                            {inq.tier === "full" ? "Full Setup $199" : "Basic $99"}
                          </span>
                          <span className="rounded-full bg-[var(--c-hover-bg)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[var(--c-muted)]">Archived</span>
                        </div>
                        <p className="mt-0.5 text-xs text-[var(--c-muted)]">
                          <a href={`mailto:${inq.email}`} className="underline decoration-dotted underline-offset-2 hover:text-[#1E3A8A]">{inq.email}</a>
                          {" "}&middot; sent {new Date(inq.created_at).toLocaleString()}
                          {inq.resolved_at && <> &middot; archived {new Date(inq.resolved_at).toLocaleString()}</>}
                        </p>
                        {inq.message && <p className="mt-1.5 rounded-md bg-[var(--c-surface)] px-2.5 py-1.5 text-xs italic text-[var(--c-text)]">&ldquo;{inq.message}&rdquo;</p>}
                      </div>
                      <button
                        data-testid={`reopen-inquiry-${inq.email}`}
                        onClick={() => reopenInquiry(inq.email)}
                        title="Move back to active inquiries"
                        className="flex flex-shrink-0 items-center gap-1 rounded-full border-2 border-[var(--c-border-light)] px-3 py-1 text-xs font-bold text-[var(--c-muted)] transition-all hover:border-[var(--c-border)] hover:text-[var(--c-text)] hover:shadow-[2px_2px_0px_var(--c-shadow)]"
                      >
                        <RotateCcw className="h-3 w-3" />
                        Reopen
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Users Table */}
        <div className="rounded-xl border-2 border-[var(--c-border)] bg-[var(--c-surface)] shadow-[4px_4px_0px_var(--c-shadow)]">
          {/* Table Header */}
          <div className="grid grid-cols-12 gap-2 border-b-2 border-[var(--c-border)] px-5 py-3 text-xs font-bold uppercase tracking-[0.15em] text-[var(--c-muted)]">
            <div className="col-span-3">Name</div>
            <div className="col-span-3">Email</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2">Joined</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>

          {/* Table Body */}
          <ScrollArea className="max-h-[60vh]">
            {customers.length === 0 ? (
              <div className="p-10 text-center text-[var(--c-muted)]">
                <Users className="mx-auto mb-3 h-10 w-10 opacity-40" />
                <p className="font-heading text-base font-bold">No customers yet</p>
                <p className="mt-1 text-sm">Create your first customer account to get started.</p>
              </div>
            ) : (
              customers.map((u) => (
                <div
                  key={u._id}
                  data-testid={`user-row-${u._id}`}
                  className="grid grid-cols-12 items-center gap-2 border-b border-[var(--c-border-light)] px-5 py-4 transition-colors hover:bg-[var(--c-hover-bg)]"
                >
                  <div className="col-span-3">
                    <p className="truncate text-sm font-semibold text-[var(--c-text)]">{u.name || "—"}</p>
                  </div>
                  <div className="col-span-3">
                    <p className="truncate text-sm text-[var(--c-muted)]">{u.email}</p>
                  </div>
                  <div className="col-span-2">
                    <span
                      data-testid={`user-status-${u._id}`}
                      className={`inline-flex items-center gap-1.5 rounded-full border-2 px-2.5 py-1 text-xs font-bold ${
                        u.is_active !== false
                          ? "border-[#A3E6D5] bg-[#A3E6D5]/15 text-[#1A1918]"
                          : "border-[#FF4D4D]/50 bg-[#FF4D4D]/10 text-[#FF4D4D]"
                      }`}
                    >
                      <span className={`h-1.5 w-1.5 rounded-full ${u.is_active !== false ? "bg-[#A3E6D5]" : "bg-[#FF4D4D]"}`} />
                      {u.is_active !== false ? "Active" : "Blocked"}
                    </span>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-[var(--c-muted)]">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                    </p>
                  </div>
                  <div className="col-span-2 flex items-center justify-end gap-1.5">
                    <button
                      data-testid={`view-progress-${u._id}`}
                      onClick={() => viewProgress(u._id)}
                      title="View progress"
                      className="flex h-8 w-8 items-center justify-center rounded-lg border-2 border-[var(--c-border-light)] text-[var(--c-muted)] transition-all hover:border-[var(--c-border)] hover:text-[#1E3A8A] hover:shadow-[2px_2px_0px_var(--c-shadow)]"
                    >
                      <Eye className="h-3.5 w-3.5" />
                    </button>
                    <button
                      data-testid={`reset-password-${u._id}`}
                      onClick={() => { setShowProgress(null); setProgressData(null); setShowCreate(false); setShowReset(u); }}
                      title="Reset password"
                      className="flex h-8 w-8 items-center justify-center rounded-lg border-2 border-[var(--c-border-light)] text-[var(--c-muted)] transition-all hover:border-[var(--c-border)] hover:text-[#1E3A8A] hover:shadow-[2px_2px_0px_var(--c-shadow)]"
                    >
                      <KeyRound className="h-3.5 w-3.5" />
                    </button>
                    <button
                      data-testid={`toggle-active-${u._id}`}
                      onClick={() => toggleActive(u._id)}
                      title={u.is_active !== false ? "Deactivate" : "Activate"}
                      className={`flex h-8 w-8 items-center justify-center rounded-lg border-2 transition-all hover:shadow-[2px_2px_0px_var(--c-shadow)] ${
                        u.is_active !== false
                          ? "border-[#FF4D4D]/30 text-[#FF4D4D] hover:border-[#FF4D4D]"
                          : "border-[#A3E6D5]/50 text-[#A3E6D5] hover:border-[#A3E6D5]"
                      }`}
                    >
                      {u.is_active !== false ? <ShieldOff className="h-3.5 w-3.5" /> : <ShieldCheck className="h-3.5 w-3.5" />}
                    </button>
                    <button
                      data-testid={`delete-user-${u._id}`}
                      onClick={() => deleteUser(u._id, u.email)}
                      title="Delete user"
                      className="flex h-8 w-8 items-center justify-center rounded-lg border-2 border-[#FF4D4D]/20 text-[#FF4D4D]/60 transition-all hover:border-[#FF4D4D] hover:text-[#FF4D4D] hover:shadow-[2px_2px_0px_var(--c-shadow)]"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </ScrollArea>
        </div>
      </div>

      {/* Create User Modal */}
      {showCreate && <CreateUserModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); loadUsers(); }} />}

      {/* Reset Password Modal */}
      {showReset && !showProgress && !showCreate && <ResetPasswordModal user={showReset} onClose={() => setShowReset(null)} />}

      {/* View Progress Modal */}
      {showProgress && progressData && !showCreate && (
        <ProgressModal data={progressData} onClose={() => { setShowProgress(null); setProgressData(null); }} />
      )}
    </div>
  );
}
